import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Archive, ArchiveRestore, LogOut, UserMinus } from 'lucide-react';
import type { ChatConversationSummary, ChatDirectoryEntry } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { chatKeys, useChat } from '@/lib/chat-provider';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PeoplePicker } from './PeoplePicker';

function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  pending,
  variant = 'outline',
  icon,
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  pending: boolean;
  variant?: 'outline' | 'destructive' | 'ghost';
  icon: React.ReactNode;
}): React.ReactElement {
  const [armed, setArmed] = React.useState(false);
  React.useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 5_000);
    return () => window.clearTimeout(timer);
  }, [armed]);
  return (
    <Button
      type="button"
      size="sm"
      variant={armed ? 'destructive' : variant}
      disabled={pending}
      onClick={() => {
        if (armed) onConfirm();
        else setArmed(true);
      }}
    >
      {icon}
      {armed ? confirmLabel : label}
    </Button>
  );
}

export function GroupSheet({
  conversation,
  open,
  onOpenChange,
}: {
  conversation: ChatConversationSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { meId, isAdmin, online } = useChat();
  const canManage = conversation.myRole === 'owner' || isAdmin;
  const archived = conversation.status === 'archived';

  const [title, setTitle] = React.useState(conversation.title ?? '');
  const [adding, setAdding] = React.useState<ChatDirectoryEntry[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setTitle(conversation.title ?? '');
  }, [conversation.title]);
  React.useEffect(() => {
    if (!open) {
      setAdding([]);
      setError(null);
    }
  }, [open]);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
      queryClient.invalidateQueries({ queryKey: chatKeys.conversation(conversation.id) }),
    ]);
  };
  const failed = (err: unknown): void => setError(errorMessage(err));

  const rename = useMutation({
    mutationFn: () => api.chatRenameGroup(conversation.id, { title: title.trim() }),
    onSuccess: refresh,
    onError: failed,
  });
  const addMembers = useMutation({
    mutationFn: () => api.chatAddMembers(conversation.id, { personIds: adding.map((p) => p.id) }),
    onSuccess: async () => {
      setAdding([]);
      await refresh();
    },
    onError: failed,
  });
  const removeMember = useMutation({
    mutationFn: (personId: string) => api.chatRemoveMember(conversation.id, personId),
    onSuccess: refresh,
    onError: failed,
  });
  const setStatus = useMutation({
    mutationFn: () =>
      archived ? api.chatUnarchiveGroup(conversation.id) : api.chatArchiveGroup(conversation.id),
    onSuccess: refresh,
    onError: failed,
  });
  const leave = useMutation({
    mutationFn: () => api.chatLeave(conversation.id),
    onSuccess: async () => {
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
      await navigate({ to: '/chat' });
    },
    onError: failed,
  });

  const titleChanged = title.trim().length > 0 && title.trim() !== (conversation.title ?? '');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{conversation.title ?? 'Group'}</SheetTitle>
          <SheetDescription>
            {conversation.members.length} {conversation.members.length === 1 ? 'member' : 'members'}
            {archived ? ' · Archived' : ''}
          </SheetDescription>
        </SheetHeader>

        {canManage && !archived ? (
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (titleChanged) rename.mutate();
            }}
          >
            <div className="flex-1">
              <Field label="Group name">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
              </Field>
            </div>
            <Button type="submit" size="sm" variant="outline" disabled={!titleChanged || rename.isPending}>
              Rename
            </Button>
          </form>
        ) : null}

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Members</h3>
          <ul className="flex flex-col divide-y divide-[hsl(var(--border))]/70 rounded-xl border border-[hsl(var(--border))]">
            {conversation.members.map((m) => (
              <li key={m.personId} className="flex items-center gap-3 px-3 py-2">
                <Avatar name={m.name} personId={m.personId} size="sm" online={online.has(m.personId)} />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {m.name}
                  {m.personId === meId ? (
                    <span className="text-[hsl(var(--muted-foreground))]"> (you)</span>
                  ) : null}
                </span>
                {m.role === 'owner' ? <Badge variant="secondary">Owner</Badge> : null}
                {canManage && !archived && m.role !== 'owner' && m.personId !== meId ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${m.name}`}
                    disabled={removeMember.isPending}
                    onClick={() => removeMember.mutate(m.personId)}
                  >
                    <UserMinus />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>

        {!archived ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Add people</h3>
            <PeoplePicker
              excludeIds={conversation.members.map((m) => m.personId)}
              selected={adding}
              onChange={setAdding}
            />
            {adding.length > 0 ? (
              <Button
                type="button"
                size="sm"
                onClick={() => addMembers.mutate()}
                disabled={addMembers.isPending}
                className="self-start"
              >
                {addMembers.isPending
                  ? 'Adding…'
                  : `Add ${adding.length} ${adding.length === 1 ? 'person' : 'people'}`}
              </Button>
            ) : null}
          </section>
        ) : null}

        {error ? <p className="text-sm text-[hsl(var(--destructive))]">{error}</p> : null}

        <div className="mt-auto flex flex-wrap gap-2 border-t border-[hsl(var(--border))]/70 pt-4">
          <ConfirmButton
            label="Leave group"
            confirmLabel="Confirm leave"
            onConfirm={() => leave.mutate()}
            pending={leave.isPending}
            icon={<LogOut />}
          />
          {canManage ? (
            <ConfirmButton
              label={archived ? 'Unarchive' : 'Archive group'}
              confirmLabel={archived ? 'Confirm unarchive' : 'Confirm archive'}
              onConfirm={() => setStatus.mutate()}
              pending={setStatus.isPending}
              icon={archived ? <ArchiveRestore /> : <Archive />}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
